import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchSchedule,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  markScheduleAs,
  finalizeSchedule,
  blockedSchedule,
} from "./thunks";
import { Schedule } from "@/types";
import { toast } from "react-toastify";
import { add } from "date-fns";

interface PaginatedSchedule {
  data: Schedule[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface ScheduleState {
  schedules: PaginatedSchedule;
  loading: boolean;
  error: string | null;
  fetched: boolean;
}

const initialState: ScheduleState = {
  schedules: { data: [] },
  loading: false,
  error: null,
  fetched: false,
};

const ScheduleSlice = createSlice({
  name: "Schedule",
  initialState,
  reducers: {
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchedule.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.fetched = true;
        const payload = action.payload;

        if (Array.isArray(payload.data)) {
          state.schedules = { data: payload.data };
        } else {
          state.schedules = {
            data: payload.data.data,
            total: payload.data.total,
            currentPage: payload.data.current_page,
            perPage: payload.data.per_page,
          };
        }
      })
      .addCase(fetchSchedule.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        state.fetched = false;
      })

      .addCase(createSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSchedule.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.schedules.data.push(action.payload.data);
          toast.success("Agendamento criado com sucesso!");
        },
      )
      .addCase(createSchedule.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Não foi possível criar o agendamento já existe um agendamento muito próximo para este funcionário, tente novamente!",
        );
      })

      .addCase(updateSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateSchedule.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.schedules.data.findIndex(
            (s) => s.id === action.payload.data.id,
          );
          if (index !== -1) {
            state.schedules.data[index] = action.payload.data;
          }
          toast.success("Agendamento atualizado com sucesso!");
        },
      )
      .addCase(updateSchedule.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Erro ao atualizar o agendamento!",
        );
      })

      .addCase(deleteSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSchedule.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.schedules.data = state.schedules.data.filter(
            (s) => String(s.id) !== action.payload.data.id,
          );
          toast.success("Agendamento excluído com sucesso!");
        },
      )
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Erro ao excluir o agendamento!",
        );
      })
      .addCase(markScheduleAs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        markScheduleAs.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          toast.success("Agendamento alterado com sucesso!");
        },
      )
      .addCase(markScheduleAs.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Erro ao alterar o agendamento!",
        );
      })

      .addCase(finalizeSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        finalizeSchedule.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          toast.success("Agendamento criado com sucesso!");
        },
      )
      .addCase(finalizeSchedule.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Não foi possível finalizar o agendamento!",
        );
      })

      .addCase(blockedSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        blockedSchedule.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          toast.success("Agendamento criado com sucesso!");
        },
      )
      .addCase(blockedSchedule.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ??
            "Não foi possível finalizar o agendamento!",
        );
      });
  },
});

export const { resetFetched } = ScheduleSlice.actions;
export default ScheduleSlice.reducer;
